// â”€â”€â”€ Contacts page (read-only, Phase 2) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Tabs: Active Â· Needs Review Â· Inactive. Search by name/email/phone.
// Filter by source (which app/process originated the contact).
//
// Behind `contacts.enabled` feature flag â€” App.tsx redirects to /users
// when the flag is off, so this page is only reachable when admin has
// flipped the flag in /admin/setup.

import React, { useEffect, useMemo, useState } from 'react';
import { fetchContacts } from '../services/contactsService';
import type { Contact, ContactSource } from '../types';
import { CONTACT_STATUS_LABELS, USER_TYPE_LABELS } from '../types';
import { formatPhoneDisplay } from '../lib/phone';
import { isSyntheticEmail } from '../lib/email';
import ContactSlideOver from '../components/ContactSlideOver';
import CreateContactModal from '../components/CreateContactModal';
import MergeContactsModal from '../components/MergeContactsModal';
import ImportContactsCsvModal, { type ImportSummary } from '../components/ImportContactsCsvModal';

type Tab = 'active' | 'needsReview' | 'inactive';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'active',      label: 'Active',        icon: 'group' },
  { id: 'needsReview', label: 'Needs Review',  icon: 'flag' },
  { id: 'inactive',    label: 'Inactive',      icon: 'person_off' },
];

// Same source enum values as src/types/contact.ts. Manually mirrored for
// the filter dropdown â€” Phase 3 will introduce a shared source-label map.
const SOURCE_FILTER_OPTIONS: { value: ContactSource | ''; label: string }[] = [
  { value: '',                    label: 'All Sources' },
  { value: 'manual',              label: 'Manual (Account Portal)' },
  { value: 'csv-import',          label: 'CSV Import' },
  { value: 'm365-sync',           label: 'Microsoft 365 Sync' },
  { value: 'okr-cascade-auto',    label: 'OKR Cascade auto-create' },
  { value: 'epr-adhoc',           label: 'EPR Ad-hoc Review' },
  { value: 'epr-cycle-import',    label: 'EPR Cycle Import' },
  { value: 'kpi-submit',          label: 'KPI Submission' },
  { value: 'migration-2026-05',   label: 'Legacy users/ migration' },
  { value: 'self-signup',         label: 'Self Signup' },
];

const ContactsPage: React.FC = () => {
  const [tab, setTab]               = useState<Tab>('active');
  const [contacts, setContacts]     = useState<Contact[]>([]);
  const [activeContacts, setActiveContacts] = useState<Contact[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterSource, setFilterSource] = useState<ContactSource | ''>('');
  const [selected, setSelected]     = useState<Contact | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [mergeFor, setMergeFor]     = useState<Contact | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [reloadKey, setReloadKey]   = useState(0);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

  // Re-fetch when tab or reloadKey changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    const filters =
      tab === 'active'
        ? { status: 'active' as const }
        : tab === 'inactive'
          ? { status: 'inactive' as const }
          : { needsReview: true };

    fetchContacts(filters)
      .then(setContacts)
      .catch((e) => setError(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  }, [tab, reloadKey]);

  // Keep an active-contacts cache for the merge picker (needs to show
  // OTHER active contacts to merge into the selected one)
  useEffect(() => {
    fetchContacts({ status: 'active' }).then(setActiveContacts).catch(() => {});
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  async function refetchSelected() {
    if (!selected) return;
    try {
      const { fetchContact } = await import('../services/contactsService');
      const fresh = await fetchContact(selected.id);
      setSelected(fresh);
    } catch {
      // ignore â€” list will refresh anyway
    }
  }

  function handleChanged() {
    void refetchSelected();
    reload();
  }

  // Client-side narrow: search + source filter
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return contacts.filter((c) => {
      const matchSource = !filterSource || c.source === filterSource;
      if (!matchSource) return false;
      if (!q) return true;
      return (
        c.displayName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.includes(q) ||
        c.department?.toLowerCase().includes(q) ||
        c.jobTitle?.toLowerCase().includes(q)
      );
    });
  }, [contacts, search, filterSource]);

  // Tab counts (only for the currently loaded tab â€” others show as "â€”")
  const currentCount = contacts.length;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-stone-900">Contacts</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            Company directory â€” people in the Rascal ecosystem
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50"
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>upload_file</span>
            Import CSV
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg transition hover:opacity-90"
            style={{ background: '#011132' }}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>person_add</span>
            New Contact
          </button>
        </div>
      </div>

      {/* Import summary banner */}
      {importSummary && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-900 flex items-center justify-between">
          <span>
            Imported {importSummary.total} row(s):{' '}
            <strong>{importSummary.created}</strong> created
            {importSummary.duplicate > 0 && <>, <strong>{importSummary.duplicate}</strong> already existed</>}
            {importSummary.errors > 0 && <>, <span className="text-red-700"><strong>{importSummary.errors}</strong> error(s)</span></>}.
          </span>
          <button onClick={() => setImportSummary(null)} className="text-emerald-700 hover:text-emerald-900">
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-stone-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${
              tab === t.id
                ? 'border-[#011132] text-[#011132] font-semibold'
                : 'border-transparent text-stone-500 hover:text-stone-800'
            }`}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
              {t.icon}
            </span>
            {t.label}
            {tab === t.id && !loading && (
              <span className="ml-1 text-xs bg-stone-100 text-stone-600 rounded-full px-2 py-0.5">
                {currentCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <span
            className="material-symbols-rounded absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            style={{ fontSize: 16 }}
          >
            search
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, deptâ€¦"
            className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm bg-white text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#011132]/20 focus:border-[#011132]"
          />
        </div>

        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value as ContactSource | '')}
          className="border border-stone-200 rounded-lg px-3 py-2.5 text-sm bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
        >
          {SOURCE_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-9 h-9 rounded-full bg-stone-200 flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-stone-200 rounded w-40" />
                  <div className="h-2.5 bg-stone-100 rounded w-56" />
                </div>
                <div className="h-6 w-14 bg-stone-100 rounded-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} hasSearch={!!search || !!filterSource} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">
                  Department
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Badges
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">
                  Source
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.photoURL ? (
                        <img src={c.photoURL} className="w-8 h-8 rounded-full flex-shrink-0" alt="" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#011132]/10 flex items-center justify-center text-[#011132] text-xs font-bold flex-shrink-0">
                          {c.displayName?.[0]?.toUpperCase() ?? '?'}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-800 text-sm truncate">
                          {c.displayName}
                        </p>
                        <p className="text-xs text-stone-400 truncate">
                          {isSyntheticEmail(c.email) ? (
                            c.phone ? (
                              <span title={c.email}>{formatPhoneDisplay(c.phone)}</span>
                            ) : (
                              <span className="italic">no contact</span>
                            )
                          ) : (
                            c.email
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {c.user_type ? (
                      <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">
                        {USER_TYPE_LABELS[c.user_type]}
                      </span>
                    ) : (
                      <span className="text-stone-300">â€”</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-stone-500 hidden lg:table-cell">
                    {c.department || <span className="text-stone-300">â€”</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span
                        className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${
                          c.status === 'active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-stone-100 text-stone-500 border-stone-200'
                        }`}
                      >
                        {CONTACT_STATUS_LABELS[c.status]}
                      </span>
                      {c.needsReview && (
                        <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border-amber-200">
                          Review
                        </span>
                      )}
                      {c.hasAppAccount && (
                        <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
                          App
                        </span>
                      )}
                      {c.isExternal && (
                        <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-orange-50 text-orange-700 border-orange-200">
                          Ext
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500 hidden md:table-cell">
                    <code>{c.source}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Slide-over */}
      <ContactSlideOver
        contact={selected}
        onClose={() => setSelected(null)}
        onChanged={handleChanged}
        onMergeRequested={(c) => setMergeFor(c)}
      />

      {/* Create modal */}
      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(contactId, wasCreated) => {
          if (!wasCreated) {
            setError(`Contact with that email/phone already exists (id: ${contactId})`);
          }
          reload();
        }}
      />

      {/* Merge modal */}
      <MergeContactsModal
        open={!!mergeFor}
        primary={mergeFor}
        candidates={activeContacts}
        onClose={() => setMergeFor(null)}
        onMerged={() => { handleChanged(); }}
      />

      {/* CSV import modal */}
      <ImportContactsCsvModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(summary) => {
          setImportSummary(summary);
          reload();
        }}
      />
    </div>
  );
};

const EmptyState: React.FC<{ tab: Tab; hasSearch: boolean }> = ({ tab, hasSearch }) => {
  if (hasSearch) {
    return (
      <div className="py-16 flex flex-col items-center gap-2">
        <span className="material-symbols-rounded text-stone-300" style={{ fontSize: 36 }}>
          search_off
        </span>
        <p className="text-sm text-stone-400">No contacts match your filters</p>
      </div>
    );
  }
  if (tab === 'needsReview') {
    return (
      <div className="py-16 flex flex-col items-center gap-2">
        <span className="material-symbols-rounded text-emerald-300" style={{ fontSize: 36 }}>
          check_circle
        </span>
        <p className="text-sm text-stone-500 font-medium">All contacts reviewed</p>
        <p className="text-xs text-stone-400">
          New auto-created contacts will appear here for HR triage.
        </p>
      </div>
    );
  }
  if (tab === 'inactive') {
    return (
      <div className="py-16 flex flex-col items-center gap-2">
        <span className="material-symbols-rounded text-stone-300" style={{ fontSize: 36 }}>
          person_off
        </span>
        <p className="text-sm text-stone-400">No inactive contacts</p>
      </div>
    );
  }
  return (
    <div className="py-16 flex flex-col items-center gap-3">
      <span className="material-symbols-rounded text-stone-300" style={{ fontSize: 36 }}>
        contacts
      </span>
      <p className="text-sm text-stone-500 font-medium">No contacts yet</p>
      <p className="text-xs text-stone-400 max-w-xs text-center">
        Contacts will appear when admins add them, or auto-grow from EPR / OKR /
        M365 sync workflows (Phase 3+).
      </p>
    </div>
  );
};

export default ContactsPage;
